/**
 * src/components/ui/Input.tsx
 * -----------------------------
 * Reusable form input with label, helper text, and error state.
 *
 * Fully implemented this will:
 *   - Render a <label> + <input> pair with correct htmlFor/id association.
 *   - Show error styling (red border, red helper text) when `error` is set.
 *   - Support `helperText` for instructions below the input.
 *   - Support all native <input> props via forwardRef.
 *   - Accept a `leftIcon` and `rightIcon` slot for icon-prefixed inputs.
 *   - Accessible: aria-invalid, aria-describedby on error messages.
 *
 * Key dependencies: clsx, tailwind-merge (via src/lib/utils.ts)
 */
import { forwardRef, useId } from 'react'
import { cn } from '@/lib/utils'
import type { InputHTMLAttributes, ReactNode } from 'react'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, leftIcon, rightIcon, className, ...props }, ref) => {
    const id = useId()
    const errorId = `${id}-error`
    const helperId = `${id}-helper`

    return (
      <div className="cx-ui-field">
        {label && (
          <label
            htmlFor={id}
            className="cx-ui-label"
          >
            {label}
          </label>
        )}

        <div className="cx-ui-input-wrap">
          {leftIcon && (
            <span className="cx-ui-input-left">
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={id}
            aria-invalid={!!error}
            aria-describedby={
              [error && errorId, helperText && helperId].filter(Boolean).join(' ') ||
              undefined
            }
            className={cn(
              'cx-ui-input',
              error && 'error',
              leftIcon && 'has-left',
              rightIcon && 'has-right',
              className,
            )}
            {...props}
          />

          {rightIcon && (
            <span className="cx-ui-input-right">
              {rightIcon}
            </span>
          )}
        </div>

        {error && (
          <p id={errorId} role="alert" className="cx-ui-error">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="cx-ui-helper">
            {helperText}
          </p>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'
